import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "@/lib/public-icons";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";

export const Route = createFileRoute("/set-new-password")({
  ssr: false,
  component: SetNewPassword,
});

/** Landing for a temporary password set by an administrator. A full page outside the shell, not a
 * dismissible dialog, because they cannot keep using an emailed password. Choosing one lifts the
 * flag: a one-way door. */
function SetNewPassword() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useQuery(orpc.auth.me.queryOptions());
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const change = useMutation(
    orpc.auth.changePassword.mutationOptions({
      onSuccess: async () => {
        toast.success("Password changed");
        await queryClient.invalidateQueries({ queryKey: orpc.auth.me.key() });
        await navigate({ to: "/", replace: true });
      },
      onError: (error) => {
        toast.error(errorWords(error));
        // The minutes after signing in may have run out: re-read, and the temporary password is
        // asked for.
        void queryClient.invalidateQueries({ queryKey: orpc.auth.me.key() });
      },
    }),
  );

  if (me.isPending) {
    return (
      <div className="bg-muted/30 min-h-dvh px-4 py-16">
        <div className="mx-auto w-full max-w-sm">
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  // Typed to sign in minutes ago, the temporary password is not asked for again (PEO-254).
  const asksTemporary = !me.data?.user.newPasswordWithoutCurrent;
  const mismatch = confirm !== "" && confirm !== newPassword;
  const incomplete = newPassword === "" || (asksTemporary && currentPassword === "");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (mismatch || incomplete) return;
    change.mutate({ currentPassword: asksTemporary ? currentPassword : undefined, newPassword });
  };

  return (
    <main className="bg-muted/30 min-h-dvh px-4 py-16">
      <div className="mx-auto w-full max-w-sm">
        <Card>
          <CardContent className="space-y-5 p-8">
            <div className="flex flex-col items-center gap-2 text-center">
              <KeyRound className="text-muted-foreground size-7" />
              <h1 className="text-lg font-semibold">Choose a new password</h1>
              <p className="text-muted-foreground text-sm">
                Your account was set up with a temporary password. Choose your own before carrying
                on.
              </p>
            </div>

            <form className="space-y-4" onSubmit={submit}>
              {asksTemporary ? (
                <div className="space-y-2">
                  <Label htmlFor="current-password">Temporary password</Label>
                  <Input
                    autoComplete="current-password"
                    id="current-password"
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    type="password"
                    value={currentPassword}
                  />
                </div>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  autoComplete="new-password"
                  id="new-password"
                  onChange={(event) => setNewPassword(event.target.value)}
                  type="password"
                  value={newPassword}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">New password again</Label>
                <Input
                  autoComplete="new-password"
                  id="confirm-password"
                  onChange={(event) => setConfirm(event.target.value)}
                  type="password"
                  value={confirm}
                />
                {mismatch ? (
                  <p className="text-destructive text-sm">The two do not match.</p>
                ) : null}
              </div>

              <Button
                className="w-full"
                disabled={change.isPending || mismatch || incomplete}
                type="submit"
              >
                {change.isPending ? "Saving…" : "Save and carry on"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
